/**
 * @fileoverview Match3 Game Session Screen
 * @description Active gameplay screen for Match3
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Vibration,
  Alert,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useMatch3,
  useMatch3Sounds,
  Match3Grid,
  Match3Header,
  Match3ResultOverlay,
  Match3Result,
  Match3GameConfig,
  Match3WeekendConfig,
  Match3TimeBonusConfig,
  DEFAULT_MATCH3_GAME_CONFIG,
  DEFAULT_MATCH3_WEEKEND_CONFIG,
} from "@/modules/match3";
import { logActivity } from "@/lib/activity-log";
import { notifyTeamOfPoints, notifyAllUsersOfScore } from "@/modules/games";

type GameMode = "free" | "weekend";

/**
 * Match3 Game Session Screen
 */
export default function Match3GameScreen() {
  const { sessionId, mode: modeParam } = useLocalSearchParams<{
    sessionId: string;
    mode: string;
  }>();
  const router = useRouter();
  const colors = useThemeColors();
  const { activeProfile } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<GameMode>((modeParam as GameMode) || "free");
  const [gameConfig, setGameConfig] = useState<Match3GameConfig>(DEFAULT_MATCH3_GAME_CONFIG);
  const [weekendConfig, setWeekendConfig] = useState<Match3WeekendConfig>(DEFAULT_MATCH3_WEEKEND_CONFIG);
  const [result, setResult] = useState<Match3Result | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [timeBonusActive, setTimeBonusActive] = useState(false);
  const [bonusSeconds, setBonusSeconds] = useState(0);
  const [userTeam, setUserTeam] = useState<{ id: string; name: string } | null>(null);
  const [activeWeekend, setActiveWeekend] = useState<{ id: string } | null>(null);
  const [familyHighScore, setFamilyHighScore] = useState(0);
  const [lottieKey, setLottieKey] = useState<string | undefined>();

  // Animation for time bonus
  const bonusAnim = useRef(new Animated.Value(0)).current;

  // Sound effects
  const {
    soundsEnabled,
    toggleSounds,
    playMatchSound,
    playBonusSound,
    isReady: soundsReady,
  } = useMatch3Sounds();

  // Track previous score to detect matches
  const prevScoreRef = useRef(0);

  /**
   * Load session data
   */
  const loadSessionData = useCallback(async () => {
    if (!sessionId || !activeProfile?.id) return;

    try {
      // Fetch session
      const { data: session, error: sessionError } = await supabase
        .from("game_sessions")
        .select("game_state, mode, weekend_id")
        .eq("id", sessionId)
        .single();

      if (sessionError) throw sessionError;

      if (session) {
        setMode(session.mode as GameMode);
        if (session.weekend_id) {
          setActiveWeekend({ id: session.weekend_id });
        }
        if (session.game_state?.config) {
          setGameConfig({
            ...DEFAULT_MATCH3_GAME_CONFIG,
            ...session.game_state.config,
          });
        }
      }

      // Fetch game definition for config
      const { data: gameDef } = await supabase
        .from("game_definitions")
        .select("default_config")
        .eq("slug", "match_3")
        .single();

      if (gameDef?.default_config) {
        setGameConfig(prev => ({
          ...prev,
          ...gameDef.default_config,
        }));
        setLottieKey(gameDef.default_config.win_lottie);
      }

      // Fetch weekend config if weekend mode
      if (session?.weekend_id) {
        const { data: weekendGame } = await supabase
          .from("weekend_game_config")
          .select("points_config, custom_config")
          .eq("weekend_id", session.weekend_id)
          .eq("game_slug", "match_3")
          .single();

        if (weekendGame) {
          const customConfig = weekendGame.custom_config as any;
          setWeekendConfig({
            ...DEFAULT_MATCH3_WEEKEND_CONFIG,
            ...(weekendGame.points_config as any),
            ...customConfig,
          });
          
          // Load custom time bonuses from weekend config if available
          if (customConfig?.time_bonuses && Array.isArray(customConfig.time_bonuses)) {
            setGameConfig(prev => ({
              ...prev,
              time_bonuses: customConfig.time_bonuses,
            }));
          }
        }

        // Fetch family high score
        const { data: statsData } = await supabase
          .from("user_game_stats")
          .select("personal_best_score")
          .eq("game_slug", "match_3")
          .eq("mode", "weekend")
          .order("personal_best_score", { ascending: false })
          .limit(1);

        if (statsData?.[0]) {
          setFamilyHighScore(statsData[0].personal_best_score || 0);
        }
      }

      // Fetch user team
      const { data: teamMember } = await supabase
        .from("team_members")
        .select(`
          team_id,
          teams:team_id (id, name)
        `)
        .eq("user_id", activeProfile.id)
        .single();

      if (teamMember?.teams) {
        setUserTeam({
          id: (teamMember.teams as any).id,
          name: (teamMember.teams as any).name,
        });
      }
    } catch (error) {
      console.error("Error loading Match3 session:", error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, activeProfile?.id]);

  useEffect(() => {
    loadSessionData();
  }, [loadSessionData]);

  /**
   * Calculate points for weekend mode
   */
  const calculatePoints = useCallback(
    (score: number, isNewFamilyRecord: boolean): { points: number; milestones: string[] } => {
      let points = weekendConfig.points_participation;
      const milestones: string[] = ["Deelname"];

      if (score >= 500) {
        points += weekendConfig.points_score_500;
        milestones.push("Score ≥ 500");
      }
      if (score >= 1000) {
        points += weekendConfig.points_score_1000;
        milestones.push("Score ≥ 1000");
      }
      if (score >= 2000) {
        points += weekendConfig.points_score_2000;
        milestones.push("Score ≥ 2000");
      }
      if (score >= 5000) {
        points += weekendConfig.points_score_5000;
        milestones.push("Score ≥ 5000");
      }
      if (isNewFamilyRecord) {
        points += weekendConfig.points_new_highscore;
        milestones.push("Nieuw familie record!");
      }

      return { points, milestones };
    },
    [weekendConfig]
  );

  /**
   * Handle time bonus earned
   */
  const handleTimeBonus = useCallback((bonus: Match3TimeBonusConfig) => {
    setBonusSeconds(bonus.bonus_seconds);
    setTimeBonusActive(true);
    Vibration.vibrate(100);
    
    // Play bonus sound for time bonus
    playBonusSound();

    // Reset after animation
    setTimeout(() => {
      setTimeBonusActive(false);
    }, 2000);
  }, [playBonusSound]);

  /**
   * Handle combo
   */
  const handleCombo = useCallback((level: number) => {
    if (level > 2) {
      Vibration.vibrate([0, 50, 50, 50]);
    }
  }, []);

  /**
   * Handle game over
   */
  const handleGameOver = useCallback(
    async (gameResult: Match3Result) => {
      if (!activeProfile?.id || !sessionId) return;

      const isNewFamilyRecord = mode === "weekend" && gameResult.score > familyHighScore;
      let pointsEarned = 0;
      let milestones: string[] = [];

      // Calculate points for weekend mode
      if (mode === "weekend") {
        const pointsCalc = calculatePoints(gameResult.score, isNewFamilyRecord);
        pointsEarned = pointsCalc.points;
        milestones = pointsCalc.milestones;
      }

      // Check if new personal high score
      const { data: existingStats } = await supabase
        .from("user_game_stats")
        .select("personal_best_score, stats")
        .eq("user_id", activeProfile.id)
        .eq("game_slug", "match_3")
        .eq("mode", mode)
        .single();

      const isNewHighScore = gameResult.score > (existingStats?.personal_best_score || 0);
      const existingStatsData = existingStats?.stats || {};

      // Update result with calculated values
      const finalResult: Match3Result = {
        ...gameResult,
        pointsEarned,
        isNewHighScore,
        isNewFamilyRecord,
        milestonesAchieved: milestones,
      };

      // Update session
      await supabase
        .from("game_sessions")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
          final_score: gameResult.score,
          points_awarded: pointsEarned,
          result: finalResult,
        })
        .eq("id", sessionId);

      // Update user stats
      const newStats = {
        games_played: (existingStatsData.games_played || 0) + 1,
        best_score: Math.max(existingStatsData.best_score || 0, gameResult.score),
        total_score: (existingStatsData.total_score || 0) + gameResult.score,
        best_combo: Math.max(existingStatsData.best_combo || 0, gameResult.maxCombo),
        total_moves: (existingStatsData.total_moves || 0) + gameResult.movesMade,
        total_time_seconds: (existingStatsData.total_time_seconds || 0) + gameResult.timePlayedSeconds,
        total_power_ups_used: (existingStatsData.total_power_ups_used || 0) + gameResult.powerUpsUsed,
        best_time_bonuses: Math.max(existingStatsData.best_time_bonuses || 0, gameResult.timeBonusesEarned),
      };

      await supabase
        .from("user_game_stats")
        .upsert({
          user_id: activeProfile.id,
          game_slug: "match_3",
          mode,
          games_played: newStats.games_played,
          personal_best_score: newStats.best_score,
          stats: newStats,
          last_played_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "user_id,game_slug,mode",
        });

      // Weekend mode: update team score and send notifications
      if (mode === "weekend" && pointsEarned > 0 && userTeam?.id) {
        // Update team score
        const { data: teamData } = await supabase
          .from("teams")
          .select("total_score")
          .eq("id", userTeam.id)
          .single();

        const previousTotal = teamData?.total_score || 0;
        const newTotal = previousTotal + pointsEarned;

        await supabase
          .from("teams")
          .update({ total_score: newTotal })
          .eq("id", userTeam.id);

        // Record in score_history
        await supabase.from("score_history").insert({
          team_id: userTeam.id,
          points: pointsEarned,
          previous_total: previousTotal,
          new_total: newTotal,
          reason: isNewFamilyRecord
            ? `🏆 Nieuw familie record! Match 3: ${gameResult.score.toLocaleString()}`
            : `Match 3 score: ${gameResult.score.toLocaleString()}`,
          game_type: "match_3",
          game_id: sessionId,
          awarded_to: activeProfile.id,
        });

        // Send notifications
        await notifyTeamOfPoints({
          earnerId: activeProfile.id,
          earnerName: activeProfile.full_name || "Iemand",
          teamId: userTeam.id,
          points: pointsEarned,
          gameName: "Match 3",
          reason: `Score: ${gameResult.score.toLocaleString()}`,
          weekendId: activeWeekend?.id,
          sessionId,
        });

        // Silent notification to all users
        if (activeWeekend?.id) {
          await notifyAllUsersOfScore({
            earnerId: activeProfile.id,
            earnerName: activeProfile.full_name || "Iemand",
            teamName: userTeam.name || "Team",
            points: pointsEarned,
            gameName: "Match 3",
            weekendId: activeWeekend.id,
            isNewRecord: isNewFamilyRecord,
          });
        }

        // Log activity
        await logActivity({
          user_id: activeProfile.id,
          action_type: "score.award",
          action_category: "score",
          target_type: "game_session",
          target_id: sessionId,
          target_name: "Match 3",
          metadata: {
            game_slug: "match_3",
            points: pointsEarned,
            score: gameResult.score,
            team_id: userTeam.id,
          },
          weekend_id: activeWeekend?.id,
        });
      }

      // Set lottie based on result
      if (isNewFamilyRecord && gameConfig.high_score_lottie) {
        setLottieKey(gameConfig.high_score_lottie);
      } else if (gameConfig.win_lottie) {
        setLottieKey(gameConfig.win_lottie);
      }

      setResult(finalResult);
      setShowResult(true);
    },
    [
      activeProfile,
      sessionId,
      mode,
      familyHighScore,
      calculatePoints,
      userTeam,
      activeWeekend,
      gameConfig,
    ]
  );

  // Initialize game hook
  const {
    grid,
    score,
    timeRemaining,
    comboLevel,
    isPlaying,
    isPaused,
    isGameOver,
    isProcessing,
    startGame,
    selectTile,
    pauseGame,
    resumeGame,
  } = useMatch3({
    config: gameConfig,
    onTimeBonus: handleTimeBonus,
    onGameOver: handleGameOver,
    onCombo: handleCombo,
  });

  // Start game when loaded
  useEffect(() => {
    if (!loading && !isPlaying && !isGameOver) {
      startGame();
    }
  }, [loading, isPlaying, isGameOver, startGame]);

  // Play match sound when score increases
  useEffect(() => {
    if (score > prevScoreRef.current && prevScoreRef.current > 0) {
      playMatchSound();
    }
    prevScoreRef.current = score;
  }, [score, playMatchSound]);

  /**
   * Handle pause toggle
   */
  const handlePauseToggle = () => {
    if (isPaused) {
      resumeGame();
    } else {
      pauseGame();
    }
  };

  /**
   * Handle play again
   */
  const handlePlayAgain = () => {
    setShowResult(false);
    setResult(null);
    startGame();
  };

  /**
   * Handle exit
   */
  const handleExit = () => {
    router.back();
  };

  /**
   * Handle back press
   */
  const handleBackPress = () => {
    if (isPlaying && !isGameOver) {
      pauseGame();
      Alert.alert(
        "Spel verlaten?",
        "Je verliest je huidige score als je stopt.",
        [
          { text: "Annuleren", onPress: resumeGame },
          { text: "Stoppen", style: "destructive", onPress: handleExit },
        ]
      );
    } else {
      handleExit();
    }
  };

  if (loading) {
    return (
      <SafeAreaView
        style={[styles.container, { backgroundColor: colors.background }]}
        edges={["top", "left", "right"]}
      >
        <Text style={{ color: colors.text }}>Laden...</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: colors.background }]}
      edges={["top", "left", "right"]}
    >
      {/* Header with back button */}
      <View style={styles.topBar}>
        {/* Left side - back button */}
        <TouchableOpacity
          onPress={handleBackPress}
          style={[styles.backButton, { backgroundColor: colors.surface }]}
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        
        {/* Center - team badge (only in weekend mode) */}
        <View style={styles.topBarCenter}>
          {mode === "weekend" && userTeam && (
            <View style={[styles.teamBadge, { backgroundColor: colors.warningLight }]}>
              <Ionicons name="people" size={14} color={colors.warning} />
              <Text style={[styles.teamText, { color: colors.warning }]}>
                {userTeam.name}
              </Text>
            </View>
          )}
        </View>

        {/* Right side - sound toggle button */}
        <TouchableOpacity
          onPress={toggleSounds}
          style={[styles.soundButton, { backgroundColor: colors.surface }]}
          accessibilityLabel={soundsEnabled ? "Geluid uitschakelen" : "Geluid inschakelen"}
          accessibilityRole="button"
        >
          <Ionicons
            name={soundsEnabled ? "volume-high" : "volume-mute"}
            size={22}
            color={soundsEnabled ? colors.primary : colors.textSecondary}
          />
        </TouchableOpacity>
      </View>

      {/* Game Header */}
      <Match3Header
        score={score}
        timeRemaining={timeRemaining}
        comboLevel={comboLevel}
        isPaused={isPaused}
        onPausePress={handlePauseToggle}
        timeBonusActive={timeBonusActive}
        bonusSeconds={bonusSeconds}
      />

      {/* Game Grid */}
      <View style={styles.gridContainer}>
        <Match3Grid
          grid={grid}
          onTilePress={selectTile}
          disabled={isPaused || isGameOver || isProcessing}
        />
      </View>

      {/* Pause Overlay */}
      {isPaused && !isGameOver && (
        <View style={styles.pauseOverlay}>
          <View
            style={[styles.pauseCard, { backgroundColor: colors.surface }]}
          >
            <Ionicons name="pause-circle" size={48} color={colors.primary} />
            <Text style={[styles.pauseTitle, { color: colors.text }]}>
              Gepauzeerd
            </Text>
            <TouchableOpacity
              style={[styles.resumeButton, { backgroundColor: colors.primary }]}
              onPress={resumeGame}
              accessibilityLabel="Doorgaan"
              accessibilityRole="button"
            >
              <Ionicons name="play" size={24} color="#FFFFFF" />
              <Text style={styles.resumeText}>Doorgaan</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Result Overlay */}
      <Match3ResultOverlay
        visible={showResult}
        result={result}
        mode={mode}
        lottieKey={lottieKey}
        onPlayAgain={handlePlayAgain}
        onExit={handleExit}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  topBarCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  soundButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  teamBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  teamText: {
    fontSize: 13,
    fontWeight: "600",
  },
  gridContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  pauseOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  pauseCard: {
    padding: 32,
    borderRadius: 20,
    alignItems: "center",
    gap: 16,
  },
  pauseTitle: {
    fontSize: 24,
    fontWeight: "bold",
  },
  resumeButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
  },
  resumeText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

